"use client";

import Image from "next/image";
import { useId, useState } from "react";
import { ArrowRight, CalendarDays, Check, CheckCircle2, MessageSquare, Search, ThumbsUp, Users } from "lucide-react";
import type { LinkedinPageCopy } from "./linkedin-page-copy";
import styles from "./linkedin-studio.module.scss";

/** A fictional, locally controlled example. No messages are sent or auto-played. */
export function CcLinkedinRelationship({ copy }: { copy: LinkedinPageCopy }) {
  const [active, setActive] = useState(0);
  const panelId = useId();
  const { experience, profile } = copy;
  const icons = [Search, ThumbsUp, MessageSquare, CalendarDays];

  return (
    <div className={styles.relationship} id="linkedin-example">
      <header className={styles.workspace}>
        <Image src="/assets/imgs/tools/linkedin.svg" width={22} height={22} alt="" />
        <span>{experience.workspace}</span>
        <CheckCircle2 size={16} aria-hidden="true" />
      </header>
      <div className={styles.relationshipCanvas} data-stage={active}>
        <div className={styles.orbit} aria-hidden="true"><i /><i /><i /></div>
        <article className={styles.profileCard}>
          <div className={styles.profileCover}><span>{experience.personLabel}</span><Users size={18} aria-hidden="true" /></div>
          <div className={styles.profilePortrait}><Image src="/assets/imgs/services/leadgenerering/personer/karin-strom.jpg" width={72} height={72} alt="" /></div>
          <div className={styles.profileInfo}>
            <strong>{profile.name}</strong><span>{profile.role}</span><small>{profile.location}</small>
            <p><Users size={14} aria-hidden="true" />{profile.mutual}</p>
          </div>
          <div className={styles.profileContext}><Search size={16} aria-hidden="true" /><div><small>{experience.contextLabel}</small><strong>{experience.context}</strong></div></div>
        </article>
        <div className={styles.statusCard}><CheckCircle2 size={17} aria-hidden="true" /><span>{experience.stages[active].status}</span></div>
        <div className={styles.events} id={panelId} aria-live="polite">
          {experience.stages.map((stage, index) => {
            const Icon = icons[index];
            return <article key={stage.label} className={styles.event} data-active={active === index} aria-hidden={active !== index} inert={active !== index}>
              <header><span><Icon size={16} aria-hidden="true" /></span><strong>{stage.label}</strong><small>{String(index + 1).padStart(2, "0")}</small></header>
              <p>{stage.event}</p>
              <footer><Check size={14} aria-hidden="true" />{stage.detail}</footer>
            </article>;
          })}
        </div>
      </div>
      <div className={styles.stageControls} role="group" aria-label={experience.progressLabel}>
        {experience.stages.map((stage, index) => <button type="button" key={stage.label} aria-pressed={active === index} aria-controls={panelId} onClick={() => setActive(index)}><span aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>{stage.label}</button>)}
      </div>
      <div className={styles.stageDetails}>
        {experience.stages.map((stage, index) => <div key={stage.label} data-active={active === index} aria-hidden={active !== index} inert={active !== index}><strong>{stage.title}</strong><p>{stage.text}</p></div>)}
      </div>
      <footer className={styles.relationshipFooter}><small>{experience.caption}</small><button type="button" onClick={() => setActive((active + 1) % experience.stages.length)}>{active === experience.stages.length - 1 ? experience.stages[0].label : experience.next}<ArrowRight size={16} aria-hidden="true" /></button></footer>
    </div>
  );
}
